// src/pages/Help.js
import Layout from '../components/common/Layout'
import { 
  QuestionMarkCircleIcon, 
  BookOpenIcon, 
  ChatBubbleLeftRightIcon,
  EnvelopeIcon 
} from '@heroicons/react/24/outline'

function FAQItem({ question, answer }) {
  return (
    <div className="border-b border-gray-200 pb-4">
      <h3 className="text-base font-medium text-gray-900 mb-2">{question}</h3>
      <p className="text-sm text-gray-600">{answer}</p>
    </div>
  )
}

export default function Help() {
  const faqs = [
    {
      question: "How does EchoGuard access my repository's issues?",
      answer: "EchoGuard operates exclusively through the official GitHub API using the permissions you grant it. When an issue is created, GitHub notifies our service, and we fetch the public issue content for real-time analysis. We do not have access to your private code."
    },
    {
      question: "Do you store the content of my GitHub issues?",
      answer: "No, never. Your issue content is processed in-memory for analysis and is immediately discarded afterward. We are committed to a strict policy of never storing your repository's issue or comment content on our servers."
    },
    {
      question: "What data do you actually store?",
      answer: "We practice data minimization. We only store two types of data: 1) Your configuration settings, like custom labels and message templates. 2) An activity log of actions taken (e.g., 'Spam detected on issue #123'). This log allows you to monitor EchoGuard's performance but does not contain the issue's content."
    },
    {
      question: "How does the analysis work without storing issue content?",
      answer: "Our AI models perform analysis on the fly. We fetch issue data from the GitHub API, run the real-time analysis, record the outcome in your activity log, and then perform the configured action (like posting a comment). The original issue content is then immediately discarded from our system's memory."
    },
    {
      question: "What happens to my data if I uninstall?",
      answer: "When you uninstall EchoGuard, all of your configuration settings and your entire activity log history are **immediately and permanently deleted** from our servers. The only things that remain are the comments and labels EchoGuard already posted to your repository, as they are now part of your GitHub history."
    },
    {
      question: "How can I verify your data access?",
      answer: "EchoGuard operates as a standard GitHub App. You have full transparency and control over the permissions it has. You can review or revoke these permissions at any time in your GitHub Settings > Applications, which would immediately cut off all access to your repository."
    }
  ]


  return (
    <Layout>
      <div className="space-y-8">
        <div className="border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">Help & Documentation</h1>
          <p className="mt-1 text-sm text-gray-600">
            Get answers to common questions and learn how to make the most of EchoGuard
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <a
            href="#getting-started"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <BookOpenIcon className="h-6 w-6 text-blue-600 mb-2" />
            <h3 className="text-sm font-medium text-gray-900">Getting Started</h3>
            <p className="text-xs text-gray-600 mt-1">Setup guide and first steps</p>
          </a>
          
          <a
            href="#faq"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <QuestionMarkCircleIcon className="h-6 w-6 text-green-600 mb-2" />
            <h3 className="text-sm font-medium text-gray-900">FAQ</h3>
            <p className="text-xs text-gray-600 mt-1">Frequently asked questions</p>
          </a>
          
          <a
            href="https://github.com/echoguard-app/EchoGuard"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <ChatBubbleLeftRightIcon className="h-6 w-6 text-purple-600 mb-2" />
            <h3 className="text-sm font-medium text-gray-900">Community</h3>
            <p className="text-xs text-gray-600 mt-1">Join discussions on GitHub</p>
          </a>
          
          <a
            href="mailto:support@echoguard.app"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <EnvelopeIcon className="h-6 w-6 text-red-600 mb-2" />
            <h3 className="text-sm font-medium text-gray-900">Contact Support</h3>
            <p className="text-xs text-gray-600 mt-1">Get help from our team</p>
          </a>
        </div>

        {/* Getting Started */}
        <div id="getting-started" className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Getting Started</h2>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                1
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Install the GitHub App</h3>
                <p className="text-sm text-gray-600">
                  Grant EchoGuard access to your repositories through GitHub's secure OAuth flow.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                2
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Configure Your Repositories</h3>
                <p className="text-sm text-gray-600">
                  Customize the behavior for each repository.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                3
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Monitor Activity</h3>
                <p className="text-sm text-gray-600">
                  Watch EchoGuard work in real-time through your activity log and GitHub notifications.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq" className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>

        {/*/!* Feature Documentation *!/*/}
        {/*<div className="bg-white border border-gray-200 rounded-lg p-6">*/}
        {/*  <h2 className="text-lg font-medium text-gray-900 mb-6">Feature Documentation</h2>*/}
        {/*  */}
        {/*  <div className="grid gap-6 md:grid-cols-2">*/}
        {/*    <div>*/}
        {/*      <h3 className="text-base font-medium text-gray-900 mb-2">Spam Check</h3>*/}
        {/*      <p className="text-sm text-gray-600 mb-3">*/}
        {/*        Automatically detects and handles spam issues using AI analysis of content patterns.*/}
        {/*      </p>*/}
        {/*      <div className="text-xs text-gray-500">*/}
        {/*        <p><strong>Actions available:</strong></p>*/}
        {/*        <ul className="ml-4 mt-1 space-y-1">*/}
        {/*          <li>• Add label and close issue</li>*/}
        {/*          <li>• Add label only</li>*/}
        {/*          <li>• Post warning comment and close</li>*/}
        {/*          <li>• Add label and post warning comment</li>*/}
        {/*        </ul>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*    */}
        {/*    <div>*/}
        {/*      <h3 className="text-base font-medium text-gray-900 mb-2">Auto-Title Generation</h3>*/}
        {/*      <p className="text-sm text-gray-600 mb-3">*/}
        {/*        AI analyzes issue content to generate clear, descriptive titles automatically.*/}
        {/*      </p>*/}
        {/*      <div className="text-xs text-gray-500">*/}
        {/*        <p><strong>Features:</strong></p>*/}
        {/*        <ul className="ml-4 mt-1 space-y-1">*/}
        {/*          <li>• Automatic title replacement</li>*/}
        {/*          <li>• Bulk regeneration for existing issues</li>*/}
        {/*          <li>• Maintains original meaning</li>*/}
        {/*          <li>• Improves searchability</li>*/}
        {/*        </ul>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*    */}
        {/*    <div>*/}
        {/*      <h3 className="text-base font-medium text-gray-900 mb-2">Similar Issue Detection</h3>*/}
        {/*      <p className="text-sm text-gray-600 mb-3">*/}
        {/*        Finds and links related issues to prevent duplicates and surface relevant discussions.*/}
        {/*      </p>*/}
        {/*      <div className="text-xs text-gray-500">*/}
        {/*        <p><strong>Plan limits:</strong></p>*/}
        {/*        <ul className="ml-4 mt-1 space-y-1">*/}
        {/*          <li>• Free: 50 checks/month, max 3 results</li>*/}
        {/*          <li>• Pro: 500 checks/month, all results</li>*/}
        {/*          <li>• Pro: Bulk checking available</li>*/}
        {/*        </ul>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*    */}
        {/*    <div>*/}
        {/*      <h3 className="text-base font-medium text-gray-900 mb-2">Feedback Inbox (Coming Soon)</h3>*/}
        {/*      <p className="text-sm text-gray-600 mb-3">*/}
        {/*        Consolidate feedback from multiple channels and convert to structured GitHub issues.*/}
        {/*      </p>*/}
        {/*      <div className="text-xs text-gray-500">*/}
        {/*        <p><strong>Planned features:</strong></p>*/}
        {/*        <ul className="ml-4 mt-1 space-y-1">*/}
        {/*          <li>• Email integration</li>*/}
        {/*          <li>• Forum/community platform sync</li>*/}
        {/*          <li>• AI analysis and categorization</li>*/}
        {/*          <li>• One-click issue creation</li>*/}
        {/*        </ul>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/* Contact Support */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-blue-900 mb-4">Still Need Help?</h2>
          
          <p className="text-sm text-blue-800 mb-4">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="mailto:support@echoguard.app"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Email Support
            </a>
            
            <a
              href="https://github.com/echoguard-app/EchoGuard/discussions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Community Discussions
            </a>
          </div>
          
          <p className="text-xs text-blue-600 mt-3">
            Pro subscribers receive priority email support with faster response times.
          </p>
        </div>
      </div>
    </Layout>
  )
}

